import { Component, Element, h, Host, Prop, Watch } from "@stencil/core";
import { MenuItemType } from "../interface";
import { findMenuItemElements } from "../utils";
import { isStringNotEmpty } from "../../../utils/type-check";

const baseClass = "uic-menu-item";

/**
 * @slot (default) - Label of the menu item. If not provided, the `label` attribute must be set (accessibility).
 */
@Component({
    tag: "uic-menu-item",
    styleUrl: "menu-item.scss",
    shadow: false
})
export class MenuItem {
    @Element() host: HTMLUicMenuItemElement;

    /** Name that must be unique in the menu scope identifies the menu-item */
    @Prop({ reflect: true }) readonly slug: string;

    /** Flag that indicates if the menu item is disabled */
    @Prop({ reflect: true }) readonly disabled: boolean = false;

    /** The level in a nested menu */
    @Prop({ reflect: true }) readonly level: number = 0;

    /** Controls if the menu item is currently selected */
    @Prop({ reflect: true }) readonly selected: boolean = false;

    /** Controls if the menu item acts as a toggle */
    @Prop({ reflect: true }) readonly toggle: boolean = false;

    /** Label text use as aria-label attribute and slot fallback */
    @Prop() readonly label?: string;

    /** If set, the menu item acts as a link */
    @Prop() readonly href?: string;

    /** Used for the link target attribute if `href` is set */
    @Prop() readonly target?: string;

    /** Used for the link download attribute if `href` is set */
    @Prop() readonly download?: string;

    /** used to show badge */
    @Prop() readonly badge?: string | number;

    /** Name of the icon that should be rendered instead of or before the label */
    @Prop() readonly icon?: string;

    /** Data provider for the menu child elements */
    @Prop() readonly items: MenuItemType[] | null = null;

    /** The parent item element in a nested menu */
    @Prop() readonly parent: HTMLUicMenuItemElement | null = null;

    /** If the item has nested items */
    @Prop() readonly expanded: boolean = false;

    /** Controls if a separator line is rendered before the item */
    @Prop() readonly separator: boolean = false;

    /** Child selected text for nested selected items */
    @Prop() readonly childSelectedText?: string;

    /** Update menu items */
    @Watch("expanded")
    setExpanded() {
        const { expanded, parent, level } = this;
        if (!expanded) {
            findMenuItemElements(this.host).forEach((el) => {
                el.expanded = false;
            });
            return;
        }
        if (parent === null) {
            return;
        }
        findMenuItemElements(parent, `[level="${level}"]`).forEach((el) => {
            if (el !== this.host) {
                el.expanded = false;
            }
        });
    }

    private renderItems() {
        const { items, level } = this;
        if (!Array.isArray(items) || items.length === 0) {
            return null;
        }
        return items.map((item) => (
            <uic-menu-item
                key={item.slug}
                parent={this.host}
                level={level + 1}
                slug={item.slug}
                label={item.label}
                icon={item.icon}
                items={item.items}
                href={item.href}
                target={item.target}
                download={item.download}
                selected={item.selected}
                separator={item.separator}
                toggle={item.toggle}
                role='menuitem'
            />
        ));
    }

    render() {
        const { childSelectedText, disabled, expanded, href, icon, items, label, selected, separator, badge } = this;
        const hasChildren = Array.isArray(items) && items.length > 0;
        const isLink = typeof href === "string" && href.length > 0;
        const ItemTag = isLink ? "a" : "div";
        const hasSelectedChild = isStringNotEmpty(childSelectedText);
        const hasLabel = isStringNotEmpty(label);
        return (
            <Host
                class={{
                    [`${baseClass}`]: true,
                    [`${baseClass}--disabled`]: disabled,
                    [`${baseClass}--icon-only`]: !hasLabel,
                    [`${baseClass}--selected`]: selected,
                    [`${baseClass}--expanded`]: expanded,
                    [`${baseClass}--separator`]: separator,
                    [`${baseClass}--child-selected`]: hasSelectedChild
                }}
                aria-disabled={disabled ? "true" : "false"}
                aria-selected={selected ? "true" : "false"}
                aria-label={label}
                aria-haspopup={hasChildren ? "true" : "false"}
                tabindex={disabled ? -1 : 0}
            >
                <ItemTag
                    class={`${baseClass}__item`}
                    href={href}
                    target={isLink ? this.target : undefined}
                    download={isLink ? this.download : undefined}
                >
                    <div class={`${baseClass}__inner`}>
                        {icon && <uic-icon class={`${baseClass}__icon`} name={icon} />}
                        {hasLabel && (
                            <span
                                class={{
                                    [`${baseClass}__label`]: true,
                                    [`${baseClass}__label--child-selected`]: hasSelectedChild
                                }}
                            >
                                {label}
                                {hasSelectedChild && <span class={`${baseClass}__label-selected-child`}>{childSelectedText}</span>}
                            </span>
                        )}
                        {((typeof badge == "string" && badge.length > 0) || (typeof badge == "number" && badge > 0)) && (
                            <uic-badge value={badge} />
                        )}
                    </div>
                    {hasChildren && <uic-icon name='uic:chevron-down' class={`${baseClass}__icon ${baseClass}__icon--expand`} />}
                    <slot name='custom-content' />
                </ItemTag>
                {hasChildren && (
                    <div
                        class={{
                            [`${baseClass}__dropdown`]: true,
                            [`${baseClass}__dropdown--expanded`]: expanded
                        }}
                        role='menu'
                        aria-hidden={expanded ? "false" : "true"}
                        aria-expanded={expanded ? "true" : "false"}
                    >
                        {this.renderItems()}
                    </div>
                )}
            </Host>
        );
    }
}
